import { supabase } from "@/integrations/supabase/client";
import {
  ActivityLibraryRow,
  GetActivityLibraryParams,
  SaveActivityToLibraryParams,
  AddLibraryActivityToItineraryParams,
  AddLibraryActivityToItineraryResult,
} from "@/integrations/supabase/rpc-types";
import {
  LibraryActivity,
  LibraryActivityFactory,
  LibraryActivityPayload,
  LibraryOwnerType,
} from "@/lib/library/models/library-activity.model";

export interface ActivityLibraryFilters {
  activityType?: string;
  tags?: string[];
  search?: string;
}

/**
 * Recupera le righe della libreria attività visibili all'utente corrente
 * (RLS già filtra per user/agency/admin), con filtro opzionale per tipo,
 * tag o ricerca testuale.
 */
export const fetchActivityLibrary = async (
  filters: ActivityLibraryFilters = {}
): Promise<LibraryActivity[]> => {
  try {
    const params: GetActivityLibraryParams = {
      p_activity_type: filters.activityType ?? null,
      p_tags: filters.tags ?? null,
      p_search: filters.search ?? null,
    };

    // RPC non ancora presente nei tipi generati (types.ts va rigenerato dopo il deploy
    // della migration) — stesso workaround già usato in useFullProfile.ts / agencyDashboard.service.ts.
    const { data, error } = await supabase.rpc('get_activity_library' as any, params);

    if (error) {
      console.error('Error fetching activity library:', error);
      throw error;
    }

    return ((data ?? []) as unknown as ActivityLibraryRow[]).map((row) =>
      LibraryActivityFactory.fromRow(row)
    );
  } catch (error) {
    console.error('Unexpected error fetching activity library:', error);
    throw error;
  }
};

/**
 * Upsert di un'attività nella libreria personale/agenzia. Se `activity.id`
 * corrisponde a una entry esistente e modificabile la RPC esegue un update,
 * altrimenti un insert.
 */
export const saveActivityToLibrary = async (
  activity: LibraryActivityPayload,
  ownerType: LibraryOwnerType,
  ownerId: string
): Promise<LibraryActivity> => {
  try {
    const params: SaveActivityToLibraryParams = {
      p_activity: activity as unknown as SaveActivityToLibraryParams['p_activity'],
      p_owner_type: ownerType,
      p_owner_id: ownerId,
    };

    const { data, error } = await supabase.rpc('save_activity_to_library' as any, params);

    if (error || !data) {
      console.error('Error saving activity to library:', error);
      throw error ?? new Error('save_activity_to_library returned no data');
    }

    return LibraryActivityFactory.fromRow(data as unknown as ActivityLibraryRow);
  } catch (error) {
    console.error('Unexpected error saving activity to library:', error);
    throw error;
  }
};

/**
 * Elimina una entry dalla libreria attività. La RLS DELETE su `activity_library`
 * (vedi migration 20260713110015) già impone che si possa cancellare solo:
 * - una entry `owner_type='user'` di proprietà propria,
 * - una entry `owner_type='agency'` se l'utente è l'account agenzia proprietario,
 * - qualunque entry se admin.
 * Qui non serve quindi una RPC dedicata: la tabella non è ancora nei tipi generati
 * (types.ts va rigenerato dopo il deploy della migration), stesso workaround `as any`
 * già usato per le RPC in questo file.
 */
export const deleteActivityFromLibrary = async (id: string): Promise<void> => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- tabella non ancora nei tipi generati Database, vedi commento sopra
    const { error } = await supabase.from('activity_library' as any).delete().eq('id', id);

    if (error) {
      console.error('Error deleting activity from library:', error);
      throw error;
    }
  } catch (error) {
    console.error('Unexpected error deleting activity from library:', error);
    throw error;
  }
};

/**
 * Copia una riga di libreria in una nuova attività dell'itinerario, in una data specifica.
 */
export const addLibraryActivityToItinerary = async (
  libraryActivityId: string,
  itineraryId: string,
  date: string
): Promise<AddLibraryActivityToItineraryResult> => {
  try {
    const params: AddLibraryActivityToItineraryParams = {
      p_library_activity_id: libraryActivityId,
      p_itinerary_id: itineraryId,
      p_date: date,
    };

    const { data, error } = await supabase.rpc('add_library_activity_to_itinerary' as any, params);

    if (error || !data) {
      console.error('Error adding library activity to itinerary:', error);
      throw error ?? new Error('add_library_activity_to_itinerary returned no data');
    }

    return data as unknown as AddLibraryActivityToItineraryResult;
  } catch (error) {
    console.error('Unexpected error adding library activity to itinerary:', error);
    throw error;
  }
};
